<template>
  <div>
    <v-card class="mb-4" style="height: 460px;">
      <v-card-title>{{ $t('controllerBreakdown') }}</v-card-title>
      <v-card-text>
        <v-chart class="chart" ref="chartRef" :option="chartOption" @click="handleChartClick" />
      </v-card-text>
    </v-card>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, defineProps, defineEmits } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { PieChart } from "echarts/charts";
import { TooltipComponent, TitleComponent, LegendComponent } from "echarts/components";
import VChart from "vue-echarts";

use([CanvasRenderer, PieChart, TooltipComponent, TitleComponent, LegendComponent]);

const emit = defineEmits(['updateFilter']);

const store = useStore();
const { t } = useI18n();
const conn = store.getters.getDBConnection;
const filterContext = computed(() => store.state.filterItemsContext);

const chartName = 'controllerTeamDonutChart';
const chartRef = ref(null);
const chartOption = ref({});
const selectedTeamId = ref(null);
const isDrillDown = ref(false);
const chartColor = computed(() => store.getters.chartFontColor);

const threshold = 0.05; // 5% threshold for showing labels

const getTeamDistributionData = async (filtered = true) => {
  const table = filtered ? 'filteredItems' : 'items';
  const query = `
    SELECT 
      i.controlledByTeamId AS teamId,
      t.name AS name,
      COUNT(*) AS value
    FROM ${table} i
    LEFT JOIN teams t ON i.controlledByTeamId = t.id
    GROUP BY i.controlledByTeamId, t.name
    ORDER BY value DESC
  `;
  
  return await executeAndProcessQuery(query);
};

const executeAndProcessQuery = async (query) => {
  try {
    const result = await conn.query(query);
    const rows = result.toArray().map(row => {
      const jsonRow = row.toJSON();
      return {
        ...jsonRow,
        value: Number(jsonRow.value),
        teamId: jsonRow.teamId,
        name: jsonRow.name || `Unknown Team (${jsonRow.teamId})`,
        itemStyle: { color: getRandomColor(jsonRow.teamId) }
      };
    });
    
    return processTeamData(rows);
  } catch (error) {
    console.error("Query execution error:", error);
    return [];
  }
};

const processTeamData = (rows) => {
  const totalItems = rows.reduce((sum, row) => sum + row.value, 0);
  const mainData = [];
  const othersData = [];

  rows.forEach(item => {
    if (item.value / totalItems >= threshold) {
      mainData.push(item);
    } else {
      othersData.push(item);
    }
  });

  if (othersData.length > 0) {
    const othersValue = othersData.reduce((sum, item) => sum + item.value, 0);
    mainData.push({
      value: othersValue,
      name: t('others'),
      itemStyle: { color: '#999' },
      children: othersData
    });
  }

  return mainData;
};

const updateChartOption = (data) => {
  chartOption.value = {
    tooltip: {
      trigger: 'item',
      confine: true,
      enterable: true,
      extraCssText: 'max-width: 320px; overflow-x: auto;',
      formatter: '{b}: {c} ({d}%)'
    },
    series: [
      {
        name: t('teamDistribution'),
        type: 'pie',
        radius: ['40%', '70%'],
        avoidLabelOverlap: true,
        padAngle: 2,
        itemStyle: {
          borderRadius: 10,
        },
        label: {
          show: true,
          position: 'outside',
          formatter: (params) => {
            return `${params.name}\n${params.value} (${params.percent.toFixed(1)}%)`;
          },
          textStyle: {
            color: chartColor.value
          }
        },
        labelLine: {
          show: true,
          length: 10,
          length2: 10,
          lineStyle: {
            color: '#bfd4e0'
          }
        },
        labelLayout: {
          hideOverlap: true
        },
        emphasis: {
          label: {
            show: true,
            fontSize: '20',
            fontWeight: 'bold'
          }
        },
        data: data
      }
    ]
  };
};

const updateFilteredItemsContext = async (teamId) => {
  if (teamId) {
    const query = `
      SELECT DISTINCT i.SubItemId 
      FROM items i
      WHERE i.controlledByTeamId = '${teamId}'
    `;
    await executeFilterQuery(query);
  } else {
    await filterContext.value.removeFilter(chartName);
  }
};

const executeFilterQuery = async (query) => {
  await filterContext.value.updateFilterWithQuery(chartName, query);
};

const handleChartClick = async (params) => {
  if (params.data) {
    if (params.data.name === t('others') && !isDrillDown.value) {
      isDrillDown.value = true;
      updateChartOption(params.data.children);
    } else {
      const teamId = params.data.teamId;
      if (teamId) {
        if (selectedTeamId.value === teamId) {
          selectedTeamId.value = null;
          await updateFilteredItemsContext();
        } else {
          selectedTeamId.value = teamId;
          await updateFilteredItemsContext(teamId);
        }
      }
      if (isDrillDown.value) {
        isDrillDown.value = false;
        const data = await getTeamDistributionData();
        updateChartOption(data);
      }
    }
  }
};

const getRandomColor = (id) => {
  const colors = ['#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5', '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', '#8BC34A', '#CDDC39', '#FFC107', '#FF9800', '#FF5722'];
  
  if (!id) {
    return colors[Math.floor(Math.random() * colors.length)];
  }
  
  const index = id.toString().split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
  return colors[index];
};

const loadChartData = async () => {
  try {
    const data = await getTeamDistributionData();
    updateChartOption(data);
  } catch (error) {
    console.error("Error loading chart data:", error);
  }
};

watch(
  () => filterContext.value.filtersLastUpdated,
  () => {
    loadChartData();
  },
  { deep: true }
);


watch(() => chartColor.value, () => {
  loadChartData();
});

onMounted(() => {
  loadChartData();
});
</script>

<style scoped>
.chart {
  height: 400px;
}
</style>